import React, { useCallback, useState } from 'react';

import { Button, Group, Paper, Stack, Text, Title, UnstyledButton } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconChevronLeft, IconUserCheck, IconUserPlus } from '@tabler/icons-react';
import { useAuth } from 'context/auth-context';

import CreateAccount from '../../../AccountController/components/CreateAccount';
import EmailVerification from '../../../AccountController/components/EmailVerification';
import ForgotPasswordModal from '../../../AccountController/components/ForgotPassword';
import Login from '../../../AccountController/components/Login';
import { useCheckout } from '../../CheckoutContext';

type AccountStepState = 'Choice' | 'Login' | 'Email Verification' | 'Create Account';

interface ChoiceCardProps {
  icon: React.ReactNode;
  title: string;
  description: string;
  dataCy: string;
  onClick: () => void;
}

const ChoiceCard = ({ icon, title, description, dataCy, onClick }: ChoiceCardProps): JSX.Element => (
  <UnstyledButton onClick={onClick} data-cy={dataCy} sx={{ flex: 1 }}>
    <Paper
      withBorder
      radius='md'
      p='md'
      h='100%'
      sx={theme => ({
        '&:hover': {
          borderColor: theme.colors.kiloblue?.[5] ?? theme.colors.blue[5],
          backgroundColor: theme.colors.gray[0],
        },
      })}
    >
      <Stack spacing='xs' align='center'>
        {icon}
        <Title order={5}>{title}</Title>
        <Text size='sm' color='dimmed' align='center'>
          {description}
        </Text>
      </Stack>
    </Paper>
  </UnstyledButton>
);

const AccountDetail = ({ label, value }: { label: string; value: string }): JSX.Element => (
  <Group position='apart' align='start' noWrap spacing='md'>
    <Text size='sm' color='dimmed' sx={{ flexShrink: 0 }}>
      {label}
    </Text>
    <Text size='sm' align='right'>
      {value}
    </Text>
  </Group>
);

/**
 * The account a signed-in buyer is purchasing with, shown read-only. A different buyer
 * logs out here and starts the checkout again on the same page.
 */
const SignedInAccount = (): JSX.Element => {
  const { loggedInAthleteFullName, loggedInAthleteEmail, signOut, isSigningOut } = useCheckout();

  return (
    <Stack spacing='xs' data-cy='accountStepSignedIn'>
      <AccountDetail label='Name' value={loggedInAthleteFullName} />
      <AccountDetail label='Email' value={loggedInAthleteEmail} />
      <Group spacing='xs'>
        <Text size='sm' color='dimmed'>
          Not you?
        </Text>
        <Button
          variant='subtle'
          compact
          data-cy='accountStepLogout'
          loading={isSigningOut}
          onClick={() => {
            signOut();
          }}
        >
          Log out
        </Button>
      </Group>
    </Stack>
  );
};

// The account gate, absorbed into the checkout accordion as Step 2 (spec Item 7,
// Variant A choice cards). Reuses the AccountController form components; completion
// is session-derived in CheckoutContext, so a successful login/create advances the
// accordion automatically.
const AccountStep = (): JSX.Element => {
  const { session } = useAuth();
  const [state, setState] = useState<AccountStepState>('Choice');
  const [verifiedEmail, setVerifiedEmail] = useState<string>('');
  const [isForgotPasswordOpen, { open: openForgotPassword, close: closeForgotPassword }] =
    useDisclosure(false);

  const handleBackToLogin = useCallback((): void => {
    closeForgotPassword();
    setState('Login');
  }, [closeForgotPassword]);

  if (session?.user) return <SignedInAccount />;

  return (
    <Stack spacing='md'>
      {state === 'Choice' && (
        <Group align='stretch' grow>
          <ChoiceCard
            icon={<IconUserPlus size='2rem' />}
            title="I'm new here"
            description='Create an account to complete your purchase.'
            dataCy='accountStepNewHere'
            onClick={() => setState('Email Verification')}
          />
          <ChoiceCard
            icon={<IconUserCheck size='2rem' />}
            title='I have an account'
            description='Sign in to continue where you left off.'
            dataCy='accountStepHaveAccount'
            onClick={() => setState('Login')}
          />
        </Group>
      )}

      {state !== 'Choice' && (
        <>
          <Button
            variant='subtle'
            w='fit-content'
            leftIcon={<IconChevronLeft size='1rem' />}
            onClick={() => setState('Choice')}
          >
            Back
          </Button>

          {state === 'Login' && <Login onForgotPassword={openForgotPassword} />}
          {state === 'Email Verification' && (
            <EmailVerification
              email={verifiedEmail}
              setVerifiedEmail={setVerifiedEmail}
              setState={next => setState(next)}
            />
          )}
          {state === 'Create Account' && (
            <CreateAccount email={verifiedEmail} handleClose={() => setState('Choice')} />
          )}
        </>
      )}

      <ForgotPasswordModal
        opened={isForgotPasswordOpen}
        onClose={closeForgotPassword}
        onBackToLogin={handleBackToLogin}
      />
    </Stack>
  );
};

export default AccountStep;
